import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Package, MapPin, ShoppingCart, Trash, PencilSimple, Eye, X, Check, FloppyDisk, CaretDown, CaretUp, Phone, FilmSlate, Wallet, WhatsappLogo, LinkSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import MobileNav from '../components/MobileNav';
import BrandLogo from '../components/BrandLogo';
import DeleteAccountSection from '../components/DeleteAccountSection';
import Footer from '../components/Footer';
import PendingReviewsCard from '../components/PendingReviewsCard';
import BookingsBoard from '../components/BookingsBoard';
import RazorpayButton from '../components/RazorpayButton';
import IdBadge from '../components/IdBadge';
import VendorOffersBoard from '../components/VendorOffersBoard';
import PackageSlideshow from '../components/PackageSlideshow';
import CustomerBookingProSlots from '../components/CustomerBookingProSlots';
import CustomerVendorInquiries from '../components/CustomerVendorInquiries';
import WalletCard from '../components/WalletCard';
import { splitCartByLane } from '../utils/cartLanes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedPackages, setSavedPackages] = useState([]);
  const [previewPackage, setPreviewPackage] = useState(null);
  const [expandedPkg, setExpandedPkg] = useState(null);
  // PowerPoint-style slideshow modal — opened from "View Slideshow" button on
  // a saved-package card. Each cart item becomes one slide showing image+price.
  const [slideshow, setSlideshow] = useState({ open: false, items: [], title: '' });
  // Wallet integration — store-credit balance fetched on mount and after any
  // booking/refund action. Apply-wallet-to-booking debits credit before the
  // Razorpay charge so customers spend their refund balance first.
  const [walletBalance, setWalletBalance] = useState(0);
  const walletRef = React.useRef(null);

  const fetchWallet = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wallet/me`, { withCredentials: true });
      setWalletBalance(Number(data?.balance) || 0);
    } catch {
      setWalletBalance(0);
    }
  }, []);
  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  // Active vendor / venue payment mode (admin-controlled). Used to choose
  // the right button label on saved packages — "Get Contact No" (free) vs
  // "Get No / Book" (advance required) per the matrix below:
  //   Mode 1 → everything free.
  //   Mode 2 → venue lines on non-wedding events require advance.
  //   Mode 3 → venue + vendor lines on non-wedding events require advance.
  const [vendorPaymentMode, setVendorPaymentMode] = useState({ mode: 1, fee: 500 });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/payments/vendor-advance-config`);
        setVendorPaymentMode({ mode: Number(data?.mode) || 1, fee: Number(data?.fee) || 500 });
      } catch { /* default mode 1 */ }
    })();
  }, []);

  const applyWallet = async (pb) => {
    const toApply = Math.min(walletBalance, Number(pb.total || 0));
    if (toApply < 1) return;
    if (!window.confirm(`Apply ₹${toApply.toLocaleString('en-IN')} from your wallet to this booking?`)) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/wallet/apply-to-booking`,
        { booking_id: pb.id, amount: toApply }, { withCredentials: true });
      if (data?.auto_paid) {
        toast.success(`Booking covered fully by wallet · ₹${toApply} debited`);
      } else {
        toast.success(`₹${data?.wallet_applied || toApply} applied · ₹${data?.amount_payable} remaining`);
      }
      fetchPackageBookings();
      fetchWallet();
      walletRef.current?.refresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    }
  };
  const [packageBookings, setPackageBookings] = useState([]);
  const [submitModalPkg, setSubmitModalPkg] = useState(null);
  const [submitForm, setSubmitForm] = useState({ customer_name: '', event_location: '', notes: '', city: '', lat: null, lng: null });
  const [submitting, setSubmitting] = useState(false);
  const [feeQuote, setFeeQuote] = useState(null);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    fetchBookings();
    fetchSavedPackages();
    fetchPackageBookings();
    loadPreviewPackage();
  }, []);

  const fetchBookings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/bookings/customer`, { withCredentials: true });
      setBookings(data);
    } catch (error) { console.error(error); }
    setLoading(false);
  };

  const fetchSavedPackages = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/packages`, { withCredentials: true });
      setSavedPackages(data || []);
    } catch {}
  };

  const fetchPackageBookings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/customer/package-bookings`, { withCredentials: true });
      setPackageBookings(data || []);
    } catch {}
  };

  /**
   * Customer marks a single saved-package cart line as booked. Backend
   * locks the vendor's calendar for the event date and notifies any
   * other customers carrying the same vendor + date that the slot is
   * now taken. After success we optimistically flip the local copy so
   * the green "Booked" pill replaces the button instantly.
   */
  const markItemBooked = async (packageId, itemIndex) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/packages/${packageId}/items/${itemIndex}/booked`,
        {},
        { withCredentials: true },
      );
      toast.success('Marked booked — vendor calendar locked');
      setSavedPackages((prev) => prev.map((p) => {
        if (p.id !== packageId) return p;
        const items = [...(p.items || [])];
        if (items[itemIndex]) {
          items[itemIndex] = { ...items[itemIndex], status: 'booked', booked_at: new Date().toISOString() };
        }
        return { ...p, items };
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark booked');
    }
  };

  const openSubmitModal = async (pkg, lane = 'all') => {
    setSubmitModalPkg({ ...pkg, _lane: lane });
    // Pre-fill the event location. Try in order:
    //   1. The city saved on this package (Instant Pricing form)
    //   2. The city the customer typed during signup (user.location)
    //   3. The last-used city saved in localStorage by InstantPricingPage
    // so the customer never has to retype something they've already said.
    let prefillCity = pkg?.event_city || pkg?.city || pkg?.eventCity || '';
    if (!prefillCity) prefillCity = user?.location || '';
    if (!prefillCity) {
      try { prefillCity = localStorage.getItem('eventbook_event_city') || ''; } catch { /* noop */ }
    }
    setSubmitForm({
      customer_name: user?.name || '',
      event_location: prefillCity,
      notes: '',
      city: prefillCity,
      lat: null,
      lng: null,
    });
    setFeeQuote(null);
    // For the pros lane, fetch a confirmation-fee quote up front so the user
    // sees what they'll pay before they hit confirm.
    if (lane === 'professionals') {
      try {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/customer/professional-bookings/quote`,
          { package_id: pkg.id },
          { withCredentials: true }
        );
        setFeeQuote(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not fetch fee quote');
      }
    }
  };

  const closeSubmitModal = () => {
    setSubmitModalPkg(null);
    setSubmitForm({ customer_name: '', event_location: '', notes: '', city: '', lat: null, lng: null });
    setFeeQuote(null);
  };

  const requestCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported on this device');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSubmitForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        toast.success('Current location captured');
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        toast.error(err?.message || 'Could not get location');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  };

  const submitPackageBooking = async () => {
    if (!submitModalPkg) return;
    if (!submitForm.customer_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    const lane = submitModalPkg._lane || 'all';
    if (lane === 'professionals' && !submitForm.city.trim()) {
      toast.error('City is required for professional bookings');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        package_id: submitModalPkg.id,
        customer_name: submitForm.customer_name.trim(),
        event_location: submitForm.event_location.trim(),
        notes: submitForm.notes.trim(),
        lane,
      };
      if (lane === 'professionals') {
        body.city = submitForm.city.trim();
        if (submitForm.lat) body.lat = submitForm.lat;
        if (submitForm.lng) body.lng = submitForm.lng;
      }
      const { data } = await axios.post(`${BACKEND_URL}/api/package-bookings`, body, { withCredentials: true });
      const laneLabel = (lane === 'professionals') ? 'Professionals'
        : (lane === 'vendors') ? 'Vendors' : 'Full package';
      toast.success(`${laneLabel} booking saved${data?.display_id ? ` — ${data.display_id}` : ''}`);

      if (lane === 'vendors' && data?.booking_id) {
        try {
          const r = await axios.post(`${BACKEND_URL}/api/customer/vendor-offers/dispatch`,
            { booking_id: data.booking_id },
            { withCredentials: true });
          toast.success(`${r.data?.offers_created || 0} vendor offer${(r.data?.offers_created || 0) === 1 ? '' : 's'} dispatched`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Booking saved, but offer dispatch failed');
        }
      } else if (lane === 'professionals' && data?.confirmation_fee?.total > 0) {
        toast.info(`Pay the ₹${Number(data.confirmation_fee.total).toLocaleString('en-IN')} confirmation fee on your booking card to lock in your professionals`);
      }
      closeSubmitModal();
      fetchPackageBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPackageBooking = async (bookingId) => {
    // Fetch refund preview first so the customer knows what they'll get back.
    let preview = null;
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/customer/package-bookings/${bookingId}/refund-preview`,
        { withCredentials: true }
      );
      preview = data;
    } catch { /* preview is best-effort */ }
    let msg = 'Cancel this booking request?';
    if (preview && preview.paid_amount > 0) {
      msg = `You've paid ₹${Number(preview.paid_amount).toLocaleString('en-IN')}. ` +
            `Refund: ₹${Number(preview.refund_amount).toLocaleString('en-IN')} (${preview.refund_pct}%).\n\n` +
            `Continue with cancellation?`;
    }
    if (!window.confirm(msg)) return;
    try {
      const { data } = await axios.delete(`${BACKEND_URL}/api/customer/package-bookings/${bookingId}`, { withCredentials: true });
      if (data?.refund?.refund_amount > 0) {
        toast.success(`Booking cancelled. Refund ₹${Number(data.refund.refund_amount).toLocaleString('en-IN')} (${data.refund.refund_pct}%) will be processed within 5–7 business days.`);
      } else {
        toast.success('Booking cancelled');
      }
      fetchPackageBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const loadPreviewPackage = () => {
    try {
      const raw = localStorage.getItem('eventbook_preview_package');
      if (raw) {
        setPreviewPackage(JSON.parse(raw));
      }
    } catch {}
  };

  const clearPreview = () => {
    localStorage.removeItem('eventbook_preview_package');
    setPreviewPackage(null);
  };

  const savePreviewAsPackage = async () => {
    if (!previewPackage) return;
    try {
      const packageName = savedPackages.length === 0 ? 'Package 1' : 'Package 2';
      await axios.post(`${BACKEND_URL}/api/packages`, {
        package_name: packageName,
        phone: previewPackage.phone,
        event_type: previewPackage.eventType,
        marriage_type: previewPackage.marriageType || '',
        event_date: previewPackage.eventDate,
        guests: parseInt(previewPackage.guestCount, 10) || 0,
        items: previewPackage.items,
        total: previewPackage.total,
      }, { withCredentials: true });
      toast.success(`Saved as ${packageName}!`);
      clearPreview();
      fetchSavedPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save package');
    }
  };

  const deletePackage = async (pkgId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/packages/${pkgId}`, { withCredentials: true });
      toast.success('Package deleted');
      fetchSavedPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const editPackageInPricing = (pkg) => {
    // Store the package data and navigate to instant pricing to edit
    const editData = {
      eventType: pkg.event_type,
      marriageType: pkg.marriage_type,
      eventDate: pkg.event_date,
      guestCount: pkg.guests,
      phone: pkg.phone,
      items: pkg.items,
      total: pkg.total,
      editingPackageId: pkg.id,
    };
    localStorage.setItem('eventbook_edit_package', JSON.stringify(editData));
    navigate('/instant-pricing');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-gray-300 text-text-secondary';
      default: return 'bg-gray-200 text-text-primary';
    }
  };

  const getCategoryColor = (cat) => {
    const colors = {
      'Decoration': 'bg-pink-100 text-pink-700 border-pink-300',
      'Catering': 'bg-orange-100 text-orange-700 border-orange-300',
      'Photography': 'bg-blue-100 text-blue-700 border-blue-300',
      'DJ & Entertainment': 'bg-purple-100 text-purple-700 border-purple-300',
      'Venue': 'bg-teal-100 text-teal-700 border-teal-300',
      'Professional': 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'Professional Staff': 'bg-indigo-100 text-indigo-700 border-indigo-300',
      'Flowers & Garlands': 'bg-rose-100 text-rose-700 border-rose-300',
      'Equipment Rental': 'bg-gray-100 text-gray-700 border-gray-300',
      'Birthday Decoration': 'bg-pink-100 text-pink-700 border-pink-300',
    };
    return colors[cat] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const PackageCard = ({ pkg, isPreview = false, onSave, onDelete, onEdit }) => {
    const isExpanded = expandedPkg === (isPreview ? 'preview' : pkg.id);
    const items = pkg.items || [];
    const { vendors: vendorItems, professionals: proItems } = splitCartByLane(items);
    // Split vendors lane visually into Venues + Other Vendors so each group
    // gets its own grouped row + dedicated action button (matches user sketch).
    const venueItems = vendorItems.filter(i => /venue|mandapam/i.test(String(i.category || '')));
    const otherVendorItems = vendorItems.filter(i => !venueItems.includes(i));
    // Mode-aware label decision — non-wedding events under modes 2/3 require
    // an advance fee before vendor/venue contact info is released.
    const isWeddingEvt = ['marriage', 'wedding', 'engagement', 'reception']
      .includes(String(pkg.event_type || '').toLowerCase());
    const venueNeedsAdvance = !isWeddingEvt && [2, 3].includes(vendorPaymentMode.mode);
    const vendorNeedsAdvance = !isWeddingEvt && vendorPaymentMode.mode === 3;

    // Render a grouped lane row: heading + chips on the LEFT, action button
    // on the RIGHT (sketch layout). On narrow screens the button drops below
    // the chips via flex-wrap so nothing gets clipped.
    const renderLaneRow = ({ laneKey, laneLabel, laneItems, accent, actionBtn }) => {
      if (laneItems.length === 0) return null;
      const subtotal = laneItems.reduce((s, i) => s + Number(i.price || 0), 0);
      const cats = [...new Set(laneItems.map((i) => i.category))];
      return (
        <div className="mb-4" data-testid={`pkg-lane-${laneKey}-${isPreview ? 'preview' : pkg.id}`}>
          <div className={`flex items-center justify-between border-l-2 ${accent} pl-2 mb-2`}>
            <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-text-primary">
              {laneLabel} <span className="text-text-secondary font-mono ml-1">({laneItems.length})</span>
            </span>
            <span className="text-sm font-bold text-brand-primary" data-testid={`pkg-lane-${laneKey}-subtotal-${isPreview ? 'preview' : pkg.id}`}>
              ₹{subtotal.toLocaleString('en-IN')}
            </span>
          </div>
          {isExpanded ? (
            <div className="space-y-2 mb-2">
              {cats.map((cat) => (
                <div key={cat}>
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-text-secondary mb-1.5">{cat}</div>
                  <div className="space-y-1">
                    {laneItems.filter((i) => i.category === cat).map((item, idx) => {
                      const globalIdx = items.findIndex((x) => x === item);
                      const isBooked = item.status === 'booked';
                      return (
                        <div key={idx}
                          className={`py-1.5 px-2 rounded text-sm border ${isBooked ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-transparent'}`}
                          data-testid={isPreview ? undefined : `pkg-item-${pkg.id}-${globalIdx}`}>
                          <div className="flex items-center justify-between">
                            <span className="flex-1 truncate">{item.name}</span>
                            {item.qty && item.qty > 1 && <span className="text-xs text-text-secondary mx-2">x{item.qty}</span>}
                            <span className="font-bold text-brand-primary ml-2">₹{Number(item.price).toLocaleString('en-IN')}</span>
                          </div>
                          {(item.vendor_id && (item.vendor_phone || isBooked)) && (
                            <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-200/60 gap-2">
                              <div className="text-[11px] text-text-secondary truncate">
                                {item.vendor_business_name && <span className="font-bold">{item.vendor_business_name}</span>}
                                {item.vendor_phone && (
                                  <a href={`tel:${item.vendor_phone}`}
                                    className="inline-flex items-center gap-1 ml-2 text-brand-primary font-medium hover:underline"
                                    data-testid={isPreview ? undefined : `vendor-phone-${pkg.id}-${globalIdx}`}>
                                    <Phone size={11} weight="fill" /> {item.vendor_phone}
                                  </a>
                                )}
                              </div>
                              {!isPreview && (
                                isBooked ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 px-1.5 py-0.5 rounded bg-emerald-100"
                                    data-testid={`pkg-item-booked-${pkg.id}-${globalIdx}`}>
                                    <Check size={11} weight="bold" /> Booked
                                  </span>
                                ) : (
                                  <Button size="sm" variant="outline"
                                    onClick={() => markItemBooked(pkg.id, globalIdx)}
                                    className="h-7 px-2 text-[11px] rounded-none border-emerald-500 text-emerald-700 hover:bg-emerald-600 hover:text-white"
                                    data-testid={`pkg-item-mark-booked-${pkg.id}-${globalIdx}`}>
                                    Mark Booked
                                  </Button>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {cats.map((cat) => {
                const catItems = laneItems.filter((i) => i.category === cat);
                const catTotal = catItems.reduce((s, i) => s + (i.price || 0), 0);
                return (
                  <span key={cat} className={`text-xs px-2 py-1 rounded border font-medium ${getCategoryColor(cat)}`}>
                    {cat} ({catItems.length}) — ₹{catTotal.toLocaleString('en-IN')}
                  </span>
                );
              })}
            </div>
          )}
          {actionBtn && <div className="mt-2">{actionBtn}</div>}
        </div>
      );
    };

    return (
      <Card className={`bg-white border-2 ${isPreview ? 'border-brand-primary' : 'border-[#E8E1D5]'}`}
        data-testid={isPreview ? 'preview-package-card' : `saved-package-${pkg.id}`}>
        <CardHeader className="pb-3 px-3 sm:px-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              {isPreview && (
                <span className="text-xs px-2 py-0.5 bg-brand-primary text-white font-bold uppercase tracking-wider rounded mb-2 inline-block">New Preview</span>
              )}
              <CardTitle className="font-heading text-xl sm:text-2xl flex items-center gap-2">
                <ShoppingCart size={22} className="text-brand-primary shrink-0" />
                <span className="truncate">{isPreview ? 'Package Preview' : pkg.name}</span>
              </CardTitle>
              <CardDescription className="mt-1 text-xs sm:text-sm">
                {pkg.event_type || pkg.eventType}{pkg.marriage_type || pkg.marriageType ? ` — ${pkg.marriage_type || pkg.marriageType}` : ''} | {pkg.guests || pkg.guest_count || pkg.guestCount} guests | {pkg.event_date || pkg.eventDate}
              </CardDescription>
            </div>
            <div className="text-right shrink-0">
              <div className="font-heading text-2xl sm:text-3xl font-bold text-brand-primary" data-testid={isPreview ? 'preview-total' : `pkg-total-${pkg.id}`}>
                ₹{Number(pkg.total).toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-text-secondary">{items.length} items</div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {/* === Grouped lanes with inline action buttons (per sketch) === */}
          {isPreview ? (
            <>
              {renderLaneRow({ laneKey: 'vendors', laneLabel: 'Vendors', laneItems: vendorItems, accent: 'border-brand-primary' })}
              {renderLaneRow({ laneKey: 'professionals', laneLabel: 'Professionals', laneItems: proItems, accent: 'border-brand-secondary' })}
            </>
          ) : (
            <>
              {/* Venues group */}
              {renderLaneRow({
                laneKey: 'venue',
                laneLabel: 'Venues',
                laneItems: venueItems,
                accent: 'border-teal-500',
                actionBtn: (
                  <Button
                    onClick={() => openSubmitModal(pkg, 'vendors')}
                    className={`w-full rounded-none ${venueNeedsAdvance ? 'bg-amber-700 hover:bg-amber-800' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                    data-testid={`submit-pkg-venue-${pkg.id}`}
                  >
                    <Phone size={16} className="mr-2" weight="fill" />
                    {venueNeedsAdvance ? `Get No / Book Venue (${venueItems.length})` : `Get Venue Contact No (${venueItems.length})`}
                  </Button>
                ),
              })}
              {/* Other vendors group */}
              {renderLaneRow({
                laneKey: 'vendors',
                laneLabel: 'Vendors',
                laneItems: otherVendorItems,
                accent: 'border-brand-primary',
                actionBtn: (
                  <Button
                    onClick={() => openSubmitModal(pkg, 'vendors')}
                    className={`w-full rounded-none ${vendorNeedsAdvance ? 'bg-amber-700 hover:bg-amber-800' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
                    data-testid={`submit-pkg-vendors-${pkg.id}`}
                  >
                    <Phone size={16} className="mr-2" weight="fill" />
                    {vendorNeedsAdvance ? `Get No / Book Vendors (${otherVendorItems.length})` : `Get Vendor Contact No (${otherVendorItems.length})`}
                  </Button>
                ),
              })}
              {/* Professionals group */}
              {renderLaneRow({
                laneKey: 'professionals',
                laneLabel: 'Professionals',
                laneItems: proItems,
                accent: 'border-brand-secondary',
                actionBtn: (
                  <Button
                    onClick={() => openSubmitModal(pkg, 'professionals')}
                    className="w-full rounded-none bg-text-primary hover:bg-text-primary/85 text-sand-50"
                    data-testid={`submit-pkg-professionals-${pkg.id}`}
                  >
                    <Check size={16} className="mr-2" />
                    Book Prof &amp; Enter ({proItems.length})
                  </Button>
                ),
              })}
            </>
          )}

          {/* === Bottom row: Slideshow + Expand/collapse all items === */}
          <div className="flex items-center gap-2 mb-3 mt-2">
            <button
              onClick={() => setSlideshow({ open: true, items, title: isPreview ? 'Package Preview' : (pkg.name || 'Package') })}
              className="flex-1 flex items-center justify-center gap-2 p-2 border-2 border-brand-primary text-brand-primary rounded text-sm font-bold hover:bg-brand-primary hover:text-sand-50 transition-colors"
              data-testid={isPreview ? 'preview-slideshow-btn' : `pkg-slideshow-${pkg.id}`}>
              <FilmSlate size={16} weight="duotone" />
              <span>View Slideshow ({items.length})</span>
            </button>
            <button
              onClick={() => setExpandedPkg(isExpanded ? null : (isPreview ? 'preview' : pkg.id))}
              className="flex-1 flex items-center justify-between p-2 border border-[#E8E1D5] rounded text-sm text-text-secondary hover:border-brand-primary/40 transition-colors"
              data-testid={isPreview ? 'preview-expand-btn' : `pkg-expand-${pkg.id}`}>
              <span>{isExpanded ? 'Hide Details' : 'View All Items'}</span>
              {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
            </button>
          </div>

          {/* === Bottom row: Preview-only Save/Edit/Discard OR Saved Edit/Delete === */}
          {isPreview ? (
            <div className="flex items-center gap-2">
              <Button onClick={onSave} className="flex-1 rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50" data-testid="save-preview-btn">
                <FloppyDisk size={16} className="mr-2" /> Save as Package
              </Button>
              <Button onClick={() => navigate('/instant-pricing')} variant="outline" className="rounded-none border-[#E8E1D5]" data-testid="edit-preview-btn">
                <PencilSimple size={16} className="mr-2" /> Edit
              </Button>
              <Button onClick={onDelete} variant="outline" className="rounded-none border-red-300 text-red-500 hover:bg-red-50" data-testid="discard-preview-btn">
                <X size={16} />
              </Button>
            </div>
          ) : (
            <>
              {/* Share row — WhatsApp + copy link to the public package page */}
              <div className="flex items-center gap-2 mb-2"
                data-testid={`pkg-share-row-${pkg.id}`}>
                {(() => {
                  const shareUrl = `${window.location.origin}/api/p/${pkg.id}/html`;
                  const messageLines = [
                    `Check out my ${pkg.event_type || 'event'} package on BeeWeddy!`,
                    `${pkg.package_name} — ₹${Math.round(pkg.total || 0).toLocaleString('en-IN')}`,
                    pkg.event_date ? `Event: ${pkg.event_date}` : null,
                    pkg.event_city ? `Location: ${pkg.event_city}` : null,
                    shareUrl,
                  ].filter(Boolean).join('\n');
                  const waHref = `https://wa.me/?text=${encodeURIComponent(messageLines)}`;
                  return (
                    <>
                      <a href={waHref} target="_blank" rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-3 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-none"
                        data-testid={`share-whatsapp-pkg-${pkg.id}`}>
                        <WhatsappLogo size={16} weight="fill" /> Share to WhatsApp
                      </a>
                      <Button variant="outline"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(shareUrl);
                            toast.success('Link copied — paste anywhere');
                          } catch {
                            toast.error('Could not copy. Long-press the WhatsApp share to grab the URL.');
                          }
                        }}
                        className="rounded-none border-[#E8E1D5] h-10"
                        data-testid={`copy-share-pkg-${pkg.id}`}>
                        <LinkSimple size={14} />
                      </Button>
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => onEdit(pkg)} variant="outline" className="flex-1 rounded-none border-[#E8E1D5]" data-testid={`edit-pkg-${pkg.id}`}>
                  <PencilSimple size={16} className="mr-2" /> Edit Package
                </Button>
                <Button onClick={() => onDelete(pkg.id)} variant="outline" className="rounded-none border-red-300 text-red-500 hover:bg-red-50" data-testid={`delete-pkg-${pkg.id}`}>
                  <Trash size={16} />
                </Button>
              </div>
              <p className="text-[10px] text-text-secondary italic mt-2">
                {(venueNeedsAdvance || vendorNeedsAdvance)
                  ? `Booking advance ₹${vendorPaymentMode.fee} per line applies for non-wedding events under the current mode.`
                  : 'Venue, Vendors and Professionals each create separate bookings — each gets its own order ID, status, and payment.'}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <PackageSlideshow
        open={slideshow.open}
        items={slideshow.items}
        title={slideshow.title}
        onClose={() => setSlideshow({ open: false, items: [], title: '' })}
      />
      <header className="glass-header sticky top-0 z-50 bg-sand-50/80 border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center">
          <BrandLogo size="md" linkTo="/" />
          <MobileNav
            links={[
              { to: '/instant-pricing', label: t('home.get_instant_pricing'), className: 'text-sm tracking-[0.2em] uppercase font-bold text-brand-primary hover:text-[#A84A38] transition-colors', testId: 'header-pricing-link' },
            ]}
            actions={[
              { onClick: logout, label: t('common.logout'), className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-logout-btn' },
            ]}
          />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-2 sm:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-light leading-none text-text-primary mb-2">
            {t('common.welcome')}, {user?.name}
          </h1>
          <div className="flex items-center gap-3 flex-wrap mt-2">
            <p className="text-base text-text-secondary">{t('customer.manage_bookings')}</p>
            {user?.display_id && <IdBadge id={user.display_id} label="Customer ID" testId="customer-id-badge" />}
          </div>
        </div>

        {/* Preview Package (from Instant Pricing) */}
        {previewPackage && (
          <div className="mb-8">
            <h2 className="font-heading text-2xl tracking-tight font-medium mb-4 flex items-center gap-2">
              <Eye size={22} className="text-brand-primary" /> Package Preview
            </h2>
            <PackageCard pkg={previewPackage} isPreview onSave={savePreviewAsPackage} onDelete={clearPreview} />
          </div>
        )}

        {/* Wallet — store credit from refunded pro slots. Hidden when empty. */}
        <WalletCard ref={walletRef} />

        {/* Saved Packages */}
        {savedPackages.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-2xl tracking-tight font-medium mb-4 flex items-center gap-2">
              <Package size={22} className="text-brand-primary" /> {t('pricing.saved_packages')}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {savedPackages.map(pkg => (
                <PackageCard key={pkg.id} pkg={pkg} onDelete={deletePackage} onEdit={editPackageInPricing} />
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mb-8 flex gap-3">
          <Link to="/instant-pricing" className="inline-flex items-center gap-2 bg-brand-primary text-sand-50 rounded-none px-6 py-3 font-medium hover:bg-[#A84A38] transition-colors" data-testid="create-package-btn">
            <ShoppingCart size={18} /> Create New Package
          </Link>
        </div>

        {/* Package Bookings (from submitted packages) */}
        {packageBookings.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-2xl tracking-tight font-medium mb-4 flex items-center gap-2">
              <Calendar size={22} className="text-brand-primary" /> My Package Bookings
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {packageBookings.map(pb => (
                <Card key={pb.id} className="bg-white border-[#E8E1D5]" data-testid={`pkg-booking-${pb.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <CardTitle className="font-heading text-xl">{pb.package_name || 'Event Package'}</CardTitle>
                      <span className={`text-xs px-2 py-1 rounded font-bold uppercase ${getStatusColor(pb.status)}`} data-testid={`pkg-booking-status-${pb.id}`}>
                        {pb.status.replace('_', ' ')}
                      </span>
                    </div>
                    {pb.display_id && (
                      <div className="mb-2 flex items-center gap-2 flex-wrap">
                        <IdBadge id={pb.display_id} label="Order" testId={`order-id-${pb.id}`} />
                        {pb.lane && pb.lane !== 'all' && (
                          <span
                            className={`text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded ${pb.lane === 'professionals' ? 'bg-text-primary text-sand-50' : 'bg-brand-primary text-sand-50'}`}
                            data-testid={`booking-lane-${pb.id}`}
                          >
                            {pb.lane === 'professionals' ? 'Professionals' : 'Vendors'}
                          </span>
                        )}
                      </div>
                    )}
                    <CardDescription className="capitalize">
                      {pb.event_type}{pb.marriage_type ? ` — ${pb.marriage_type}` : ''} | {pb.guests} guests | {pb.event_date}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {pb.event_location && (
                      <div className="flex items-center gap-2 text-sm text-text-secondary">
                        <MapPin size={14} /> {pb.event_location}
                      </div>
                    )}
                    {pb.notes && (
                      <div className="text-sm text-text-secondary italic">"{pb.notes}"</div>
                    )}

                    {/* Vendor offer board — only renders for the vendors lane */}
                    <VendorOffersBoard
                      booking={pb}
                      onSwapLine={(booking, line) => {
                        const pkg = savedPackages.find((sp) => sp.id === booking.package_id);
                        if (!pkg) {
                          toast.error('Original package not found — open Instant Pricing manually');
                          return;
                        }
                        const payload = {
                          eventType: pkg.event_type,
                          marriageType: pkg.marriage_type,
                          eventDate: pkg.event_date,
                          guestCount: pkg.guests,
                          phone: pkg.phone,
                          items: pkg.items || [],
                          total: pkg.total,
                          editingPackageId: pkg.id,
                          swapItemId: line.cart_item_id,
                          swapCategory: line.category,
                        };
                        try { localStorage.setItem('eventbook_edit_package', JSON.stringify(payload)); } catch { /* noop */ }
                        navigate('/instant-pricing');
                      }}
                    />

                    <div className="mt-3 pt-3 border-t border-[#E8E1D5] flex items-center justify-between flex-wrap gap-2">
                      {pb.lane === 'professionals' && pb.confirmation_fee_amount > 0 ? (
                        <div data-testid={`pkg-booking-advance-${pb.id}`}>
                          <div className="font-heading text-2xl text-brand-primary">
                            ₹{Number(pb.confirmation_fee_amount).toLocaleString('en-IN')}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-text-secondary">
                            Advance · Full Salary (Incl. commission)
                          </div>
                          <div className="text-[10px] text-text-secondary italic mt-0.5">
                            Line-item prices below are for reference. Actual payable = the advance.
                          </div>
                        </div>
                      ) : (
                        <div className="font-heading text-2xl text-brand-primary" data-testid={`pkg-booking-total-${pb.id}`}>
                          ₹{Number(pb.total).toLocaleString('en-IN')}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Vendor-lane upfront advance — Mode 2 / Mode 3 non-wedding event.
                            Customer pays a flat ₹X per qualifying line before the inquiry
                            fans out to vendors. Vendor declines auto-refund to wallet. */}
                        {pb.lane === 'vendors' && pb.vendor_advance_amount > 0 && pb.vendor_advance_status === 'pending_payment' && (
                          <RazorpayButton
                            bookingId={pb.id}
                            purpose="vendor_advance"
                            description={`Vendor advance — ${pb.package_name || 'Booking'}`}
                            testId={`pay-vendor-advance-${pb.id}`}
                            customer={{ name: user?.name || '', contact: user?.phone || '', email: user?.email || '' }}
                            onSuccess={() => { fetchPackageBookings(); toast.success('Advance paid · vendors notified!'); }}
                            label={`Pay ₹${Number(pb.vendor_advance_amount).toLocaleString('en-IN')} advance`}
                            className="text-sm"
                          />
                        )}
                        {pb.lane === 'vendors' && pb.vendor_advance_status === 'paid' && (
                          <span className="text-[10px] uppercase tracking-wider font-bold bg-green-600 text-white px-2 py-0.5 rounded" data-testid={`vendor-adv-paid-${pb.id}`}>
                            Advance Paid ✓
                          </span>
                        )}
                        {pb.lane === 'professionals' && pb.confirmation_fee_amount > 0 && pb.confirmation_fee_status !== 'paid' && (
                          <RazorpayButton
                            bookingId={pb.id}
                            purpose="confirmation_fee"
                            description={`Confirmation fee — ${pb.package_name || 'Professionals'}`}
                            testId={`pay-confirmation-fee-${pb.id}`}
                            customer={{ name: user?.name || '', contact: user?.phone || '', email: user?.email || '' }}
                            onSuccess={() => { fetchPackageBookings(); toast.success('Professionals locked in!'); }}
                            label={`Pay ₹${Number(pb.confirmation_fee_amount).toLocaleString('en-IN')} fee`}
                            className="text-sm"
                          />
                        )}
                        {pb.lane === 'professionals' && pb.confirmation_fee_status === 'paid' && (
                          <span className="text-[10px] uppercase tracking-wider font-bold bg-green-600 text-white px-2 py-0.5 rounded" data-testid={`confirmation-paid-${pb.id}`}>
                            Confirmation Paid ✓
                          </span>
                        )}
                        {(pb.status === 'confirmed' || pb.status === 'in_progress') && pb.lane !== 'professionals' && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Apply wallet credit before paying. Only shown when there
                                is unused credit AND wallet hasn't been applied yet. */}
                            {walletBalance > 0 && pb.payment_status !== 'paid' && !pb.wallet_applied && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => applyWallet(pb)}
                                className="rounded-none border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-sand-50"
                                data-testid={`apply-wallet-${pb.id}`}>
                                <Wallet size={14} className="mr-1" /> Apply ₹{Math.min(walletBalance, pb.total).toLocaleString('en-IN')}
                              </Button>
                            )}
                            <RazorpayButton
                              bookingId={pb.id}
                              description={`${pb.package_name || 'Event Package'} — ${pb.event_type}`}
                              paid={pb.payment_status === 'paid'}
                              testId={`pay-pkg-booking-${pb.id}`}
                              customer={{ name: user?.name || '', contact: user?.phone || '', email: user?.email || '' }}
                              onSuccess={() => { fetchPackageBookings(); walletRef.current?.refresh?.(); }}
                              label={pb.payment_status === 'paid'
                                ? 'Paid'
                                : `Pay ₹${Number(pb.amount_payable ?? pb.total).toLocaleString('en-IN')}`}
                              className="text-sm"
                            />
                            {pb.wallet_applied > 0 && (
                              <span className="text-[10px] tracking-wider uppercase font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded">
                                Wallet · ₹{Number(pb.wallet_applied).toLocaleString('en-IN')} applied
                              </span>
                            )}
                          </div>
                        )}
                        {(pb.status === 'pending' || pb.status === 'confirmed') && pb.payment_status !== 'paid' && (
                          <Button onClick={() => cancelPackageBooking(pb.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-500 hover:bg-red-50" data-testid={`cancel-pkg-booking-${pb.id}`}>
                            Cancel
                          </Button>
                        )}
                      </div>

                      {/* Per-pro confirmation tracker — only when the professional
                          lane confirmation fee is paid (slots are dispatched then). */}
                      {pb.lane === 'professionals' && pb.confirmation_fee_status === 'paid' && (
                        <div className="mt-3">
                          <CustomerBookingProSlots booking={pb}
                            onChange={() => { fetchPackageBookings(); fetchWallet(); walletRef.current?.refresh?.(); }} />
                        </div>
                      )}

                      {/* Per-vendor inquiry tracker — vendor-lane bookings, dispatched
                          on creation (Mode 1 / wedding) or after advance verify (Mode 2/3). */}
                      {pb.lane === 'vendors' && (
                        <div className="mt-3">
                          <CustomerVendorInquiries booking={pb}
                            onChange={() => { fetchPackageBookings(); fetchWallet(); walletRef.current?.refresh?.(); }} />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Bookings */}
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-medium mb-4 flex items-center gap-2">
            <Calendar size={22} className="text-brand-primary" /> {t('customer.your_bookings')}
          </h2>
          {loading ? (
            <div className="text-center py-12">
              <div className="font-heading text-xl text-text-secondary">{t('common.loading')}</div>
            </div>
          ) : bookings.length === 0 ? (
            <Card className="bg-white border-[#E8E1D5]">
              <CardContent className="py-12 text-center">
                <p className="text-base text-text-secondary mb-4">{t('customer.no_bookings')}</p>
                <Link to="/instant-pricing" className="text-brand-primary hover:underline">
                  {t('home.get_instant_pricing')}
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {bookings.map((booking) => (
                <Card key={booking.id} className="bg-white border-[#E8E1D5]" data-testid={`booking-card-${booking.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <CardTitle className="font-heading text-xl">{booking.vendor_business_name || booking.vendor_name}</CardTitle>
                      <span className={`text-xs px-2 py-1 rounded font-bold uppercase ${getStatusColor(booking.status)}`} data-testid={`booking-status-${booking.id}`}>
                        {booking.status}
                      </span>
                    </div>
                    <CardDescription>{booking.service_name}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Calendar size={14} /> {new Date(booking.event_date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Package size={14} /> <span className="capitalize">{booking.event_type}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#E8E1D5]">
                      <div className="font-heading text-2xl text-brand-primary" data-testid={`booking-price-${booking.id}`}>
                        ₹{booking.total_price}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8">
          <PendingReviewsCard testId="customer-pending-reviews" emptyLabel="No pending ratings. After your captain marks an event complete, you'll be asked to rate the vendors." />
          <div className="mt-6">
            <BookingsBoard viewerRole="customer" title="My Bookings" testId="customer-bookings-board" />
          </div>
        </div>

        <DeleteAccountSection />
      </div>

      <Footer />

      {/* Submit Booking Modal */}
      {submitModalPkg && (() => {
        const allItems = submitModalPkg.items || [];
        const { vendors: vL, professionals: pL } = splitCartByLane(allItems);
        const lane = submitModalPkg._lane || 'all';
        const laneItems = lane === 'vendors' ? vL : lane === 'professionals' ? pL : allItems;
        const laneTotal = laneItems.reduce((s, i) => s + Number(i.price || 0), 0);
        const laneLabel = lane === 'vendors' ? 'Vendors' : lane === 'professionals' ? 'Professionals' : 'Full Package';
        const laneBadgeClass = lane === 'vendors'
          ? 'bg-brand-primary text-sand-50'
          : lane === 'professionals'
          ? 'bg-text-primary text-sand-50'
          : 'bg-gray-700 text-sand-50';
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeSubmitModal} data-testid="submit-booking-modal">
          <div className="bg-white max-w-md w-full border border-[#E8E1D5] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#E8E1D5]">
              <h3 className="font-heading text-xl font-medium">
                {lane === 'vendors' ? 'Get Vendor Phone Numbers'
                 : lane === 'professionals' ? 'Lock In Professionals'
                 : `Submit ${laneLabel} Booking`}
              </h3>
              <button onClick={closeSubmitModal} className="text-text-secondary hover:text-text-primary" data-testid="close-submit-modal">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-text-secondary">
                <span className={`inline-block text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded ${laneBadgeClass} mb-2`} data-testid="submit-lane-badge">
                  {laneLabel} only · {laneItems.length} item{laneItems.length === 1 ? '' : 's'}
                </span><br />
                <span className="font-medium text-text-primary">{submitModalPkg.package_name || 'Event Package'}</span><br />
                <span className="capitalize">{submitModalPkg.event_type}</span> • {submitModalPkg.guests || submitModalPkg.guest_count} guests • {submitModalPkg.event_date}
                <br /><span className="font-heading text-xl text-brand-primary" data-testid="submit-lane-total">₹{Number(laneTotal).toLocaleString('en-IN')}</span>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Your Name *</label>
                <input
                  type="text"
                  value={submitForm.customer_name}
                  onChange={(e) => setSubmitForm({ ...submitForm, customer_name: e.target.value })}
                  className="w-full bg-white border border-[#E8E1D5] rounded-none px-4 py-3 text-text-primary focus:ring-1 focus:ring-brand-primary outline-none"
                  data-testid="submit-customer-name"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Event Location</label>
                <input
                  type="text"
                  placeholder="e.g. Coimbatore"
                  value={submitForm.event_location}
                  onChange={(e) => setSubmitForm({ ...submitForm, event_location: e.target.value })}
                  className="w-full bg-white border border-[#E8E1D5] rounded-none px-4 py-3 text-text-primary focus:ring-1 focus:ring-brand-primary outline-none"
                  data-testid="submit-event-location"
                />
              </div>

              {lane === 'professionals' && (
                <>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">City <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Chennai"
                      value={submitForm.city}
                      onChange={(e) => setSubmitForm({ ...submitForm, city: e.target.value })}
                      className="w-full bg-white border border-[#E8E1D5] rounded-none px-4 py-3 text-text-primary focus:ring-1 focus:ring-brand-primary outline-none"
                      data-testid="submit-city"
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={requestCurrentLocation}
                      disabled={locating}
                      className="w-full inline-flex items-center justify-center gap-2 border border-[#E8E1D5] hover:border-brand-primary px-4 py-2 text-sm rounded-none"
                      data-testid="submit-current-location"
                    >
                      <MapPin size={14} />
                      {locating ? 'Detecting…' : (submitForm.lat ? `Pinned ✓ (${submitForm.lat.toFixed(4)}, ${submitForm.lng.toFixed(4)})` : 'Use Current Location (optional)')}
                    </button>
                  </div>

                  {feeQuote && (
                    <div className="border border-amber-300 bg-amber-50 rounded p-3 space-y-1" data-testid="fee-quote">
                      <div className="text-xs tracking-[0.18em] uppercase font-bold text-amber-900 mb-1">Confirmation fee</div>
                      {feeQuote.pros > 0 && (
                        <div className="flex justify-between text-sm">
                          <span>{feeQuote.pros} professional{feeQuote.pros === 1 ? '' : 's'} × ₹{feeQuote.pro_fee_each.toLocaleString('en-IN')}</span>
                          <span className="font-mono">₹{feeQuote.pro_subtotal.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      {feeQuote.waiters > 0 && (
                        <div className="flex justify-between text-sm">
                          <span>{feeQuote.waiters} server/waiter × ₹{feeQuote.waiter_fee_each.toLocaleString('en-IN')}</span>
                          <span className="font-mono">₹{feeQuote.waiter_subtotal.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm text-amber-900/80">
                        <span>Platform commission ({feeQuote.commission_pct}%)</span>
                        <span className="font-mono">+ ₹{feeQuote.commission_amount.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold text-amber-900 pt-1 border-t border-amber-300" data-testid="fee-quote-total">
                        <span>Total to pay</span>
                        <span className="font-mono">₹{feeQuote.total.toLocaleString('en-IN')}</span>
                      </div>
                      <p className="text-[10px] text-amber-900/70 italic">
                        Paid before professionals are dispatched. Refund: 50% if cancelled 3+ days before event, 25% otherwise.
                      </p>
                    </div>
                  )}
                </>
              )}
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 block">Special Notes</label>
                <textarea
                  rows={3}
                  placeholder="Any special requirements..."
                  value={submitForm.notes}
                  onChange={(e) => setSubmitForm({ ...submitForm, notes: e.target.value })}
                  className="w-full bg-white border border-[#E8E1D5] rounded-none px-4 py-3 text-text-primary focus:ring-1 focus:ring-brand-primary outline-none"
                  data-testid="submit-notes"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={submitPackageBooking} disabled={submitting} className="flex-1 rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50" data-testid="confirm-submit-booking">
                  {submitting ? 'Submitting...' : (
                    lane === 'vendors' ? 'Send Offers to Vendors'
                    : lane === 'professionals' ? (feeQuote ? `Continue → Pay ₹${Number(feeQuote.total).toLocaleString('en-IN')}` : 'Continue')
                    : `Confirm ${laneLabel} Booking`
                  )}
                </Button>
                <Button onClick={closeSubmitModal} variant="outline" className="rounded-none border-[#E8E1D5]" data-testid="cancel-submit-booking">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default CustomerDashboard;
